import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaPlus, FaPencilAlt } from 'react-icons/fa';
import { useTeamChatSocket } from '../../hooks/useTeamChatSocket';
import { useApi } from '../../state/ApiContext';
import { useChatDock } from '../../state/ChatDockContext';
import { useChatActivity } from '../../state/ChatActivityContext';
import { formatSentTime } from '../../lib/relative-time';
import type {
  CardRef,
  CostPricingState,
  VoiceAgentOption,
  VoiceConversationCostRollup,
} from '../../client';
import { CARD_REF_PATTERN } from '../../lib/card-refs';
import { wikiUrl } from '../../lib/wiki-refs';
import { useTeamRefContext } from '../../hooks/useTeamRefContext';
import ChatSurface from './ChatSurface';
import { conversationVoiceAgents } from './VoiceAgentSelector';
import '../../agent-chat.css';
import { sessionFetch } from '../../session-client';
import type { VoiceCostState } from './voiceCost';

interface TeamChatProps {
  teamId: string;
}

interface ConversationSummary {
  id: number;
  title: string;
  updatedAt?: string;
  totalCostUsd: number | null;
  knownTotalCostUsd: number;
  costPricingState: CostPricingState;
  metricCount: number;
  /** Origin page of a wiki_page conversation (highlight-to-chat) */
  wikiPage?: { projectId: number; pagePath: string } | null;
}

interface VoiceCostSnapshot {
  conversationId: number | null;
  lifecycleSignature: string;
  rollup: VoiceConversationCostRollup | null;
  state: VoiceCostState;
}

const VOICE_COST_SESSION_PAGE_LIMIT = 500;
const VOICE_COST_MINIMAL_DETAIL_LIMIT = 1;
const VOICE_COST_PENDING_POLL_MS = 1_000;
const VOICE_COST_PENDING_POLL_MAX_ATTEMPTS = 12;

/**
 * Unified real-time chat with a team's agents: @mentions route messages,
 * agents can hand off to each other, and the exchange guard pauses long
 * agent-to-agent chains for approval. The header picks past conversations.
 */
const TeamChat: React.FC<TeamChatProps> = ({ teamId }) => {
  // Lifecycle log: a remount destroys the socket and transcript, which is
  // catastrophic for a live chat — make it visible in consoles and traces
  React.useEffect(() => {
    console.log(`[chat] TeamChat mounted (team ${teamId})`);
    return () => console.log(`[chat] TeamChat unmounted (team ${teamId})`);
  }, [teamId]);

  const chatSocket = useTeamChatSocket(teamId);
  const {
    items,
    connectionState,
    conversationId,
    conversationTitle,
    isProcessing,
    startNewConversation,
    selectConversation,
  } = chatSocket;
  const lifecycleSignature = items
    .filter((item) => item.voiceLifecycle)
    .map(
      (item) =>
        `${item.voiceLifecycle?.sessionId}:${item.voiceLifecycle?.event}:${item.messageId ?? item.key}`,
    )
    .join('|');

  const { chatApi, defaultApi, metricsApi, projectsApi, teamApi } = useApi();
  const {
    requestedConversation,
    consumeRequestedConversation,
    requestedMemberChat,
    consumeRequestedMemberChat,
    requestedAgentChat,
    consumeRequestedAgentChat,
    requestedWikiChat,
    consumeRequestedWikiChat,
  } = useChatDock();
  const [composerSeed, setComposerSeed] = useState<{
    text: string;
    nonce: number;
  } | null>(null);
  const [initialTargetAgent, setInitialTargetAgent] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [composerFocusNonce, setComposerFocusNonce] = useState<number | null>(
    null,
  );
  const [voiceAgents, setVoiceAgents] = useState<VoiceAgentOption[]>([]);
  const { unreadByConversation, markConversationRead } = useChatActivity();
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const saveTitle = useCallback(async () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (!conversationId || !next) return;
    // Optimistically update the picker label + list
    setHistory((current) =>
      current.map((conversation) =>
        conversation.id === conversationId
          ? { ...conversation, title: next }
          : conversation,
      ),
    );
    try {
      await sessionFetch(
        `/api/teams/${teamId}/chat-conversations/${conversationId}`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: next }),
        },
      );
    } catch {
      // best-effort; the label already reflects the intent
    }
  }, [titleDraft, conversationId, teamId]);

  // Reading the open conversation clears its unread chat mentions
  useEffect(() => {
    if (conversationId && (unreadByConversation.get(conversationId) ?? 0) > 0) {
      markConversationRead(conversationId);
    }
    // Re-run when new items arrive in the open conversation (a mention
    // that lands while you're looking should clear too)
  }, [
    conversationId,
    items.length,
    unreadByConversation,
    markConversationRead,
  ]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<ConversationSummary[]>([]);
  /**
   * Wiki origin of a just-started highlight-to-chat conversation: the
   * history list doesn't know the new conversation yet, so the header
   * chip is fed from here until it does.
   */
  const [pendingWikiOrigin, setPendingWikiOrigin] = useState<{
    projectId: number;
    pagePath: string;
  } | null>(null);
  const { cardRefs, projects, refreshCardRefs } = useTeamRefContext(teamId);
  const [teamUsers, setTeamUsers] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [agentsMentionable, setAgentsMentionable] = useState(false);
  const historyRef = useRef<HTMLDivElement>(null);
  const activeConversationIdRef = useRef(conversationId);
  const historyRequestRef = useRef(0);
  const voiceCostRequestRef = useRef(0);
  const historyRefreshTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [voiceCostSnapshot, setVoiceCostSnapshot] = useState<VoiceCostSnapshot>(
    {
      conversationId: null,
      lifecycleSignature: '',
      rollup: null,
      state: 'loading',
    },
  );
  const lifecycleSignatureRef = useRef(lifecycleSignature);
  activeConversationIdRef.current = conversationId;
  lifecycleSignatureRef.current = lifecycleSignature;
  const voiceCost =
    voiceCostSnapshot.conversationId === conversationId
      ? voiceCostSnapshot.rollup
      : null;
  const voiceCostState: VoiceCostState =
    voiceCostSnapshot.conversationId === conversationId &&
    voiceCostSnapshot.lifecycleSignature === lifecycleSignature
      ? voiceCostSnapshot.state
      : 'loading';
  const voiceCostSettlementPending = Boolean(
    voiceCost?.sessions.some((session) => session.settlementPending),
  );

  // A pending "open this conversation" request (notification link,
  // conversations list) targeting this team: switch to it and consume
  useEffect(() => {
    if (!requestedConversation || requestedConversation.teamId !== teamId) {
      return;
    }
    if (requestedConversation.conversationId !== conversationId) {
      setPendingWikiOrigin(null);
      setInitialTargetAgent(null);
      setComposerFocusNonce(null);
      selectConversation(requestedConversation.conversationId);
    }
    consumeRequestedConversation();
  }, [
    requestedConversation,
    teamId,
    conversationId,
    selectConversation,
    consumeRequestedConversation,
  ]);

  // Agent-list Chat: always open a fresh conversation with an invisible
  // structured first-message target. This must not depend on items.length:
  // the dock remounts when opened from its launcher and its previous transcript
  // can arrive after this request. A blank seed clears and focuses the composer
  // without making the person type after a synthetic @mention.
  useEffect(() => {
    if (!requestedAgentChat || requestedAgentChat.teamId !== teamId) return;
    setPendingWikiOrigin(null);
    startNewConversation();
    setInitialTargetAgent(requestedAgentChat.agent);
    setComposerFocusNonce(requestedAgentChat.nonce);
    setComposerSeed({ text: '', nonce: requestedAgentChat.nonce });
    consumeRequestedAgentChat();
  }, [
    requestedAgentChat,
    teamId,
    startNewConversation,
    consumeRequestedAgentChat,
  ]);

  // A pending "chat with this member" request (member detail modal):
  // open a fresh conversation and pre-address them in the composer. An
  // already-empty conversation is reused — starting a new one from it
  // would pile up empty conversation rows (the header's New-chat button
  // has the same guard).
  useEffect(() => {
    if (!requestedMemberChat || requestedMemberChat.teamId !== teamId) {
      return;
    }
    if (items.length > 0) {
      setPendingWikiOrigin(null);
      startNewConversation();
    }
    setInitialTargetAgent(null);
    setComposerFocusNonce(null);
    setComposerSeed({
      text: `@${requestedMemberChat.mentionName} `,
      nonce: requestedMemberChat.nonce,
    });
    consumeRequestedMemberChat();
  }, [
    requestedMemberChat,
    teamId,
    items.length,
    startNewConversation,
    consumeRequestedMemberChat,
  ]);

  // Highlight-to-chat: open a NEW conversation carrying the wiki-page
  // origin (the current channel, even when empty, lacks the scope) and
  // seed the composer with the quoted selection.
  useEffect(() => {
    if (!requestedWikiChat || requestedWikiChat.teamId !== teamId) {
      return;
    }
    startNewConversation({ wikiScope: requestedWikiChat.wikiScope });
    setPendingWikiOrigin(requestedWikiChat.wikiScope);
    setInitialTargetAgent(null);
    setComposerFocusNonce(null);
    setComposerSeed({
      text: requestedWikiChat.seedText,
      nonce: requestedWikiChat.nonce,
    });
    consumeRequestedWikiChat();
  }, [
    requestedWikiChat,
    teamId,
    startNewConversation,
    consumeRequestedWikiChat,
  ]);

  // Cards created DURING the conversation (e.g. AIR building a project
  // board while you chat) aren't in the mount-time ref list, so their
  // #KEY-N mentions would render as dead text. When a finished message
  // cites a reference we can't resolve, refresh the list — once per
  // unknown token, so a genuinely bogus ref can't cause a refetch loop.
  const attemptedRefRefreshes = useRef<Set<string>>(new Set());
  useEffect(() => {
    const known = new Set(
      cardRefs.map((ref) => (ref.reference || '').toUpperCase()),
    );
    const pattern = new RegExp(CARD_REF_PATTERN.source, 'g');
    const unknown: string[] = [];
    for (const item of items) {
      if (item.kind !== 'agent' || !item.finalContent) continue;
      for (const match of item.finalContent.matchAll(pattern)) {
        const token = match[0].toUpperCase();
        if (!known.has(token) && !attemptedRefRefreshes.current.has(token)) {
          unknown.push(token);
        }
      }
    }
    if (unknown.length === 0) return;
    unknown.forEach((token) => attemptedRefRefreshes.current.add(token));
    refreshCardRefs();
  }, [items, cardRefs, refreshCardRefs]);

  // Team members for the @mention autocomplete (mentioning one notifies
  // them); negative ids so they never collide with agent ids
  useEffect(() => {
    let cancelled = false;
    setAgentsMentionable(false);
    teamApi
      .apiTeamsGet()
      .then((response) => {
        if (cancelled) return;
        const team = response.data.find(
          (candidate) => candidate.id === parseInt(teamId, 10),
        );
        setAgentsMentionable(
          !!team && (team as any).workingStyle !== 'no_agents',
        );
      })
      .catch(() => {
        if (!cancelled) setAgentsMentionable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamApi, teamId]);

  useEffect(() => {
    let active = true;
    setVoiceAgents([]);
    if (!agentsMentionable || !defaultApi?.listTeamVoiceOptions) {
      return () => {
        active = false;
      };
    }
    defaultApi
      .listTeamVoiceOptions(parseInt(teamId, 10))
      .then((response) => {
        if (!active) return;
        setVoiceAgents(response.data.agents || []);
      })
      .catch(() => {
        if (active) setVoiceAgents([]);
      });
    return () => {
      active = false;
    };
  }, [agentsMentionable, defaultApi, teamId]);

  useEffect(() => {
    teamApi
      .apiTeamTeamIdUsersGet(parseInt(teamId, 10))
      .then((response) =>
        setTeamUsers(
          (response.data || [])
            .filter((user) => user.id)
            .map((user) => ({
              id: -user.id!,
              name:
                `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                user.email ||
                `User ${user.id}`,
            })),
        ),
      )
      .catch(() => setTeamUsers([]));
  }, [teamApi, teamId]);

  const loadHistory = useCallback(async () => {
    const requestId = ++historyRequestRef.current;
    const requestedConversationId = activeConversationIdRef.current;
    try {
      const response = await chatApi.apiTeamsTeamIdChatConversationsGet(
        parseInt(teamId, 10),
      );
      if (
        requestId !== historyRequestRef.current ||
        requestedConversationId !== activeConversationIdRef.current
      ) {
        return;
      }
      setHistory(
        (response.data.conversations || []).map((conversation) => ({
          id: conversation.id!,
          title: conversation.title || 'New chat',
          updatedAt: conversation.updatedAt,
          totalCostUsd: conversation.totalCostUsd,
          knownTotalCostUsd:
            conversation.knownTotalCostUsd ?? conversation.totalCostUsd ?? 0,
          costPricingState:
            conversation.costPricingState ??
            (conversation.totalCostUsd === null ? 'unpriced' : 'priced'),
          metricCount: conversation.metricCount ?? 0,
          wikiPage:
            conversation.wikiPage?.projectId && conversation.wikiPage?.pagePath
              ? {
                  projectId: conversation.wikiPage.projectId,
                  pagePath: conversation.wikiPage.pagePath,
                }
              : null,
        })),
      );
    } catch (error) {
      if (
        requestId !== historyRequestRef.current ||
        requestedConversationId !== activeConversationIdRef.current
      ) {
        return;
      }
      console.error('Failed to load chat history:', error);
      setHistory([]);
    }
  }, [chatApi, teamId]);

  const loadVoiceCost = useCallback(async () => {
    const requestId = ++voiceCostRequestRef.current;
    const requestedConversationId = activeConversationIdRef.current;
    const requestedLifecycleSignature = lifecycleSignatureRef.current;
    if (requestedConversationId == null) {
      setVoiceCostSnapshot({
        conversationId: null,
        lifecycleSignature: requestedLifecycleSignature,
        rollup: null,
        state: 'ready',
      });
      return;
    }
    setVoiceCostSnapshot((current) => ({
      conversationId: requestedConversationId,
      lifecycleSignature: requestedLifecycleSignature,
      rollup:
        current.conversationId === requestedConversationId
          ? current.rollup
          : null,
      state: 'loading',
    }));
    const requestIsStale = (): boolean =>
      requestId !== voiceCostRequestRef.current ||
      requestedConversationId !== activeConversationIdRef.current ||
      requestedLifecycleSignature !== lifecycleSignatureRef.current;
    try {
      let sessionOffset = 0;
      let latestRollup: VoiceConversationCostRollup | null = null;
      let sessionTotal = 0;
      const sessions = new Map<
        string,
        VoiceConversationCostRollup['sessions'][number]
      >();
      do {
        if (requestIsStale()) return;
        const response =
          await metricsApi.apiTeamTeamIdMetricsVoiceCostsConversationIdGet(
            parseInt(teamId, 10),
            requestedConversationId,
            VOICE_COST_SESSION_PAGE_LIMIT,
            sessionOffset,
            VOICE_COST_MINIMAL_DETAIL_LIMIT,
            0,
            VOICE_COST_MINIMAL_DETAIL_LIMIT,
            0,
          );
        if (requestIsStale()) return;
        if (response.data.conversationId !== requestedConversationId) {
          throw new Error('Voice cost response did not match the conversation');
        }
        latestRollup = response.data;
        response.data.sessions.forEach((session) =>
          sessions.set(session.sessionId, session),
        );
        sessionTotal = response.data.detailPage.sessions.total;
        const nextOffset =
          response.data.detailPage.sessions.offset +
          response.data.sessions.length;
        if (
          response.data.sessions.length === 0 ||
          nextOffset >= sessionTotal ||
          nextOffset <= sessionOffset
        ) {
          break;
        }
        sessionOffset = nextOffset;
      } while (sessionOffset < sessionTotal);
      if (requestIsStale() || latestRollup === null) return;
      const rollup: VoiceConversationCostRollup = {
        ...latestRollup,
        sessions: [...sessions.values()],
        detailPage: {
          ...latestRollup.detailPage,
          sessions: {
            limit: VOICE_COST_SESSION_PAGE_LIMIT,
            offset: 0,
            total: sessionTotal,
          },
        },
      };
      setVoiceCostSnapshot({
        conversationId: requestedConversationId,
        lifecycleSignature: requestedLifecycleSignature,
        rollup,
        state: 'ready',
      });
    } catch (error) {
      if (requestIsStale()) return;
      console.error('Failed to load voice costs:', error);
      setVoiceCostSnapshot((current) => {
        const canRetainCurrentRollup =
          current.conversationId === requestedConversationId &&
          current.lifecycleSignature === requestedLifecycleSignature &&
          current.rollup !== null;
        return {
          conversationId: requestedConversationId,
          lifecycleSignature: requestedLifecycleSignature,
          rollup: canRetainCurrentRollup ? current.rollup : null,
          state: canRetainCurrentRollup ? 'ready' : 'error',
        };
      });
    }
  }, [metricsApi, teamId]);

  const scheduleHistoryRefresh = useCallback(() => {
    if (historyRefreshTimerRef.current) {
      clearTimeout(historyRefreshTimerRef.current);
    }
    historyRefreshTimerRef.current = setTimeout(() => {
      historyRefreshTimerRef.current = undefined;
      void loadHistory();
      void loadVoiceCost();
    }, 50);
  }, [loadHistory, loadVoiceCost]);

  // Load the conversation list up front so the header title resolves
  // immediately (not only once the dropdown is opened).
  useEffect(() => {
    if (historyRefreshTimerRef.current) {
      clearTimeout(historyRefreshTimerRef.current);
      historyRefreshTimerRef.current = undefined;
    }
    void loadHistory();
    void loadVoiceCost();
    return () => {
      historyRequestRef.current += 1;
      voiceCostRequestRef.current += 1;
    };
  }, [loadHistory, loadVoiceCost, conversationId]);

  useEffect(
    () => () => {
      if (historyRefreshTimerRef.current) {
        clearTimeout(historyRefreshTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (conversationId == null || !voiceCostSettlementPending) return;
    let active = true;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = (): void => {
      if (!active || attempts >= VOICE_COST_PENDING_POLL_MAX_ATTEMPTS) return;
      attempts += 1;
      void loadVoiceCost().finally(() => {
        if (!active || attempts >= VOICE_COST_PENDING_POLL_MAX_ATTEMPTS) {
          return;
        }
        timer = setTimeout(poll, VOICE_COST_PENDING_POLL_MS);
      });
    };
    timer = setTimeout(poll, VOICE_COST_PENDING_POLL_MS);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [
    conversationId,
    lifecycleSignature,
    loadVoiceCost,
    voiceCostSettlementPending,
  ]);

  const settledTurnSignature = items
    .filter((item) => item.kind === 'agent' && item.status === 'complete')
    .map((item) => `${item.assistantMessageId ?? item.key}`)
    .join('|');
  const settledTurnsRef = useRef<{
    conversationId: number | null;
    signature: string;
  }>();
  useEffect(() => {
    const previous = settledTurnsRef.current;
    settledTurnsRef.current = {
      conversationId,
      signature: settledTurnSignature,
    };
    if (
      conversationId != null &&
      previous?.conversationId === conversationId &&
      previous.signature !== settledTurnSignature
    ) {
      scheduleHistoryRefresh();
    }
  }, [conversationId, scheduleHistoryRefresh, settledTurnSignature]);

  const lifecycleRef = useRef<{
    conversationId: number | null;
    signature: string;
  }>();
  useEffect(() => {
    const previous = lifecycleRef.current;
    lifecycleRef.current = { conversationId, signature: lifecycleSignature };
    if (
      conversationId != null &&
      previous?.conversationId === conversationId &&
      previous.signature !== lifecycleSignature
    ) {
      scheduleHistoryRefresh();
    }
  }, [conversationId, lifecycleSignature, scheduleHistoryRefresh]);

  useEffect(() => {
    if (!historyOpen) return;
    // Refresh on open so a conversation renamed/created by another
    // participant shows up even when conversationId hasn't changed.
    void loadHistory();
    const onMouseDown = (event: MouseEvent) => {
      if (!historyRef.current?.contains(event.target as Node)) {
        setHistoryOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [historyOpen, loadHistory]);

  const activeTitle =
    history.find((conversation) => conversation.id === conversationId)?.title ??
    conversationTitle ??
    'Chat';

  // The open conversation's wiki origin: from the history list once it
  // knows the conversation, else from the just-started request.
  const activeWikiPage =
    (conversationId != null
      ? history.find((conversation) => conversation.id === conversationId)
          ?.wikiPage
      : null) ?? pendingWikiOrigin;

  const conversationCost =
    conversationId == null
      ? undefined
      : history.find((conversation) => conversation.id === conversationId);

  return (
    <div className="agent-chat">
      <div className="chat-header">
        <div className="chat-header-left">
          {conversationId != null && !editingTitle && (
            <button
              className="chat-title-edit-button"
              onClick={() => {
                setTitleDraft(activeTitle === 'Chat' ? '' : activeTitle);
                setEditingTitle(true);
              }}
              data-testid="chat-title-edit"
              title="Rename this conversation">
              <FaPencilAlt size={12} />
            </button>
          )}
          {editingTitle ? (
            <input
              className="chat-title-input"
              data-testid="chat-title-input"
              autoFocus
              value={titleDraft}
              placeholder="Conversation name"
              onChange={(event) => setTitleDraft(event.target.value)}
              onBlur={saveTitle}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void saveTitle();
                } else if (event.key === 'Escape') {
                  setEditingTitle(false);
                }
              }}
            />
          ) : (
            <div className="chat-history-picker" ref={historyRef}>
              <button
                className="chat-history-button"
                onClick={() => setHistoryOpen((open) => !open)}
                data-testid="chat-history-menu"
                title="Conversation history">
                <span className="chat-history-title">{activeTitle}</span>
                <FaChevronDown size={9} />
              </button>
              {historyOpen && (
                <div
                  className="chat-history-options"
                  data-testid="chat-history-options">
                  {history.map((conversation) => (
                    <button
                      key={conversation.id}
                      className={`chat-history-option ${
                        conversation.id === conversationId
                          ? 'chat-history-option-active'
                          : ''
                      }`}
                      onClick={() => {
                        setHistoryOpen(false);
                        if (conversation.id !== conversationId) {
                          setPendingWikiOrigin(null);
                          setInitialTargetAgent(null);
                          setComposerFocusNonce(null);
                          selectConversation(conversation.id);
                        }
                      }}
                      data-testid={`chat-history-option-${conversation.id}`}>
                      {(unreadByConversation.get(conversation.id) ?? 0) > 0 && (
                        <span
                          className="chat-history-unread"
                          data-testid={`chat-history-unread-${conversation.id}`}>
                          {unreadByConversation.get(conversation.id)}
                        </span>
                      )}
                      <span className="chat-history-option-main">
                        <span className="chat-history-option-title">
                          {conversation.title}
                        </span>
                        {conversation.updatedAt && (
                          <span className="chat-history-option-time">
                            {formatSentTime(conversation.updatedAt)}
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                  {history.length === 0 && (
                    <span className="chat-history-empty">No past chats</span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {activeWikiPage && (
          <Link
            className="chat-wiki-origin-chip"
            data-testid="chat-wiki-origin-chip"
            title="This conversation started from a wiki page"
            to={wikiUrl(
              teamId,
              activeWikiPage.projectId,
              activeWikiPage.pagePath,
            )}>
            {'📄 '}
            {activeWikiPage.pagePath.split('/').pop()}
          </Link>
        )}
        <button
          className="chat-new-conversation"
          onClick={() => {
            setPendingWikiOrigin(null);
            setInitialTargetAgent(null);
            setComposerFocusNonce(null);
            startNewConversation();
          }}
          disabled={connectionState !== 'connected' || items.length === 0}
          title="Start a new conversation">
          <FaPlus size={11} /> New chat
        </button>
      </div>
      <ChatSurface
        teamId={teamId}
        chat={chatSocket}
        cardRefs={cardRefs}
        projects={projects}
        agentsEnabled={agentsMentionable}
        mentionables={teamUsers}
        composerSeed={composerSeed}
        composerFocusNonce={composerFocusNonce}
        initialTargetAgent={initialTargetAgent}
        onInitialTargetUsed={() => {
          setInitialTargetAgent(null);
          setComposerFocusNonce(null);
        }}
        voiceAgents={conversationVoiceAgents(voiceAgents, items)}
        conversationCost={conversationCost}
        voiceCost={
          voiceCost?.conversationId === conversationId ? voiceCost : null
        }
        voiceCostState={voiceCostState}
        onDurableCostRefreshSignal={scheduleHistoryRefresh}
      />
    </div>
  );
};

export default TeamChat;
